import React from "react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Activity,
  HeartPulse,
  Dumbbell,
  Shield,
  CheckCircle,
  ArrowRight,
  Star,
  Trophy,
} from "lucide-react";
import { clinicData } from "../mock";
import { useNavigate } from "react-router-dom";
import Header from "./Header";

const SportsInjuryRecoverySection = () => {
  const navigate = useNavigate();
  const handleBookAppointment = () => navigate("/book-appointment");

  const benefits = [
    {
      icon: HeartPulse,
      title: "Faster Recovery",
      description:
        "Cut rehabilitation time significantly with targeted treatment plans",
    },
    {
      icon: Dumbbell,
      title: "Strength Restoration",
      description:
        "Regain muscle strength and flexibility with personalized therapy",
    },
    {
      icon: Shield,
      title: "Injury Prevention",
      description:
        "Reduce the risk of re-injury with expert guidance and conditioning",
    },
    {
      icon: Trophy,
      title: "Peak Performance",
      description:
        "Return to your sport stronger and more confident than before",
    },
  ];

  // Defensive fallback for packages
  const sportsRecoveryPackages = clinicData?.sportsRecoveryPackages || [];

  return (
    <>
      <Header hideBookButton={true} />
      <section className="py-20 bg-gradient-to-br from-white via-slate-50 to-blue-50 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-[#204080]/5 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#2E5AAC]/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-[#204080]/10 text-[#204080] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Activity size={16} />
              <span>Sports Injury Recovery</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-[#204080] mb-6 font-serif">
              Get Back in the Game
              <span className="block bg-clip-text bg-gradient-to-r from-[#204080] to-[#2E5AAC]">
                Faster & Stronger
              </span>
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Whether you're a professional athlete or a weekend warrior, our
              sports injury recovery programs help you heal, restore strength,
              and prevent future injuries.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <Card
                  key={index}
                  className="group p-6 text-center bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="space-y-4">
                    <div className="inline-flex p-3 bg-gradient-to-br from-[#204080] to-[#2E5AAC] rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <IconComponent size={24} className="text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-[#204080] group-hover:text-[#2E5AAC] transition-colors duration-300">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Package Cards */}
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {sportsRecoveryPackages.map((pkg) => (
              <Card
                key={pkg.id}
                className={`group relative overflow-hidden ${
                  pkg.popular ? "ring-2 ring-[#2E5AAC] scale-105" : ""
                } bg-white shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border-0`}
              >
                {pkg.popular && (
                  <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-10">
                    <Badge className="bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white border-0 px-2 py-0.5 shadow-lg rounded-full text-xs font-semibold flex items-center">
                      <Star size={10} className="mr-1" />
                      <span style={{ fontSize: "0.75rem" }}>Most Popular</span>
                    </Badge>
                  </div>
                )}

                <div
                  className={`p-8 ${
                    pkg.popular
                      ? "bg-gradient-to-br from-[#204080] to-[#2E5AAC] text-white"
                      : "bg-gradient-to-br from-slate-50 to-blue-50"
                  }`}
                >
                  <div className="text-center space-y-4">
                    <h3
                      className={`text-2xl font-bold ${
                        pkg.popular ? "text-white" : "text-[#204080]"
                      }`}
                    >
                      {pkg.name}
                    </h3>
                    <p
                      className={`text-sm ${
                        pkg.popular ? "text-blue-100" : "text-gray-600"
                      }`}
                    >
                      {pkg.duration}
                    </p>
                    <div
                      className={`text-lg font-semibold ${
                        pkg.popular ? "text-blue-100" : "text-[#204080]"
                      }`}
                    >
                      {pkg.price}
                    </div>
                  </div>
                </div>

                <div className="p-8 space-y-6">
                  {pkg.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <CheckCircle
                        size={16}
                        className="text-[#2E5AAC] flex-shrink-0"
                      />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}

                  <button
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
                      pkg.popular
                        ? "bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white hover:shadow-xl transform hover:scale-105"
                        : "border-2 border-[#204080] text-[#204080] hover:bg-[#204080] hover:text-white"
                    }`}
                    onClick={handleBookAppointment}
                  >
                    Start Recovery
                  </button>
                </div>
              </Card>
            ))}
          </div>

          {/* Success Metrics */}
          <Card className="p-8 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white border-0 shadow-2xl mb-16">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-4">
                Proven Recovery Results
              </h3>
              <p className="text-blue-100 text-lg">
                Helping athletes return stronger every day
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">90%</div>
                <div className="text-blue-100 text-sm">Full Recovery Rate</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">75%</div>
                <div className="text-blue-100 text-sm">
                  Faster Return to Sport
                </div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">60%</div>
                <div className="text-blue-100 text-sm">
                  Lower Risk of Re-Injury
                </div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">500+</div>
                <div className="text-blue-100 text-sm">Athletes Treated</div>
              </div>
            </div>
          </Card>

          {/* CTA */}
          <div className="text-center">
            <Card className="max-w-4xl mx-auto p-8 bg-white/90 backdrop-blur-sm border border-gray-200 shadow-2xl">
              <h3 className="text-2xl font-bold text-[#204080] mb-4">
                Ready to Start Your Recovery Journey?
              </h3>
              <p className="text-gray-600 mb-8 text-lg">
                Book your consultation today and let our expert team help you
                get back to doing what you love.
              </p>
              <div className="flex justify-center">
                <button
                  className="flex items-center justify-center space-x-2 px-8 py-3 bg-gradient-to-r from-[#204080] to-[#2E5AAC] text-white font-semibold rounded-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  onClick={() =>
                    navigate("/book-appointment", {
                      state: { selectedService: "Sports Recovery Session" },
                    })
                  }
                >
                  <span>Book Sports Recovery Session</span>
                  <ArrowRight size={16} />
                </button>
              </div>
            </Card>
          </div>
        </div>
      </section>
    </>
  );
};

export default SportsInjuryRecoverySection;
